import React, { useState, useEffect, useCallback } from 'react';
import { Users, TrendingUp, DollarSign, Filter, X, Trash2, AlertTriangle, Loader2, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import PageHeader from './ui-shared/PageHeader';
import FilterPanel from './ui-shared/FilterPanel';
import DateRangeFilter from './ui-shared/DateRangeFilter';
import ExportMenu from './ui-shared/ExportMenu';
import CustomerProfileDrawer from './CustomerProfileDrawer';
import SortableHeader, { useTableSort } from './SortableHeader';
import { CustomerBadges, useNewCustomerDays } from './CustomerBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 50;

export default function AdminCustomers({ token }) {
  const newCustomerDays = useNewCustomerDays(token);
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    first_name: '',
    surname: '',
    email: '',
    account_id: '',
    user_id: '',
    country: '',
    is_company: '',
    is_active: '',
    customer_group_id: '',
    date_start: '',
    date_end: '',
    reserved_state: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // customer object | null
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deletePin, setDeletePin] = useState('');
  const [deleteMode, setDeleteMode] = useState('account');   // 'account' | 'purge'
  const [deleteBlacklist, setDeleteBlacklist] = useState(false);
  const [deletePreview, setDeletePreview] = useState(null);

  // Fetch "what will be removed" as soon as the confirm modal opens, so an
  // admin can never nuke a live customer by accident.
  useEffect(() => {
    if (!confirmDelete) { setDeletePreview(null); return; }
    setDeleteMode('account');
    setDeleteBlacklist(false);
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/admin/customers/${confirmDelete.id}/delete-preview`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDeletePreview(r.ok ? await r.json() : null);
      } catch (_) { setDeletePreview(null); }
    })();
  }, [confirmDelete, token]);
  const [pinRequired, setPinRequired] = useState(false);
  const [profileCustomerId, setProfileCustomerId] = useState(null);
  const [rankMap, setRankMap] = useState({});
  const [totalRanked, setTotalRanked] = useState(0);
  // Pagination — `/admin/customers` paginates via skip/limit; we track the
  // current page and a total count so the UI renders "Page X of Y" correctly.
  const [page, setPage] = useState(1);
  // Server-side sort — sends sort_by / sort_dir to /api/admin/customers/filter.
  // Resets page to 1 on every sort change to keep behavior intuitive.
  const { sortBy, sortDir, onSortChange } = useTableSort({
    onPageReset: () => setPage(1),
  });
  const [totalCount, setTotalCount] = useState(0);
  // Dropdown options — countries come from the shared orders/filters/options
  // endpoint (same one that feeds Orders/Tickets/Transactions), so newly
  // imported customer countries show up automatically.
  const [pickerOptions, setPickerOptions] = useState({ countries: [], groups: [], accounts: [] });

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/admin/customers/rank-map`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) {
          const d = await r.json();
          setRankMap(d.ranks || {});
          setTotalRanked(d.total_customers || 0);
        }
      } catch (_) { /* silent */ }
    })();
  }, [token]);

  // Determine if a customer-delete PIN is configured. If so, the delete modal
  // shows a PIN input that must be filled in.
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/admin/pins`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) return;
        const d = await r.json();
        setPinRequired(Boolean((d?.customer_delete || '').trim()));
      } catch (_) { /* silent */ }
    })();
  }, [token]);

  // Load country + group dropdowns once (shared with Orders/Tickets/Transactions)
  useEffect(() => {
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          fetch(`${API_URL}/api/orders/filters/options`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_URL}/api/customer-groups/`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        const d1 = r1.ok ? await r1.json() : {};
        const d2 = r2.ok ? await r2.json() : [];
        setPickerOptions({
          countries: d1.countries || [],
          groups: Array.isArray(d2) ? d2 : (d2.items || []),
          accounts: d1.accounts || [],
        });
      } catch (_) { /* silent */ }
    })();
  }, [token]);

  // Only the *applied* filters hit the backend. Typing into a text input
  // updates `filters` (visual state) but the page only refetches when the
  // user explicitly hits "Filter" / changes a dropdown / changes page.
  // This avoids a fetch-per-keystroke storm that froze the UI on slow
  // connections (iter-29 regression).
  const [appliedFilters, setAppliedFilters] = useState(filters);
  // Dropdown filters (country / group / is_company / is_active) should
  // auto-apply — they change infrequently and the user expects an instant
  // result. We sync them to appliedFilters via an effect but leave the
  // text inputs (first_name / surname / email / account_id) pending until
  // Filter / Enter.
  useEffect(() => {
    setAppliedFilters((prev) => ({
      ...prev,
      country:           filters.country,
      customer_group_id: filters.customer_group_id,
      is_company:        filters.is_company,
      is_active:         filters.is_active,
      user_id:           filters.user_id,
      date_start:        filters.date_start,
      date_end:          filters.date_end,
      reserved_state:    filters.reserved_state,
    }));
    setPage(1);
  }, [filters.country, filters.customer_group_id, filters.is_company, filters.is_active, filters.user_id, filters.date_start, filters.date_end, filters.reserved_state]);

  const loadCustomers = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const p = opts.page || page;
      const skip = (p - 1) * PAGE_SIZE;
      const queryParams = new URLSearchParams();
      Object.entries(appliedFilters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
      queryParams.append('skip', String(skip));
      queryParams.append('limit', String(PAGE_SIZE));
      // Server-side sort (sort_by / sort_dir) — empty when no column is
      // active so the endpoint's default `registration_date desc` governs.
      if (sortBy) {
        queryParams.append('sort_by', sortBy);
        if (sortDir) queryParams.append('sort_dir', sortDir);
      }

      const response = await fetch(`${API_URL}/api/admin/customers/filter?${queryParams}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (Array.isArray(data)) {
        setCustomers(data);
        try {
          const countParams = new URLSearchParams(queryParams);
          countParams.set('limit', '1');
          countParams.set('skip', '0');
          const r2 = await fetch(`${API_URL}/api/admin/customers/count?${countParams}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (r2.ok) {
            const d = await r2.json();
            setTotalCount(d.total ?? d.count ?? 0);
          } else {
            setTotalCount(stats.total_customers || data.length);
          }
        } catch (_) { setTotalCount(stats.total_customers || data.length); }
      } else {
        setCustomers(data.items || []);
        setTotalCount(data.total ?? data.count ?? 0);
      }
    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page, token, stats.total_customers, sortBy, sortDir]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/customers/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [token]);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  // "Filter" button — pushes whatever's in the text inputs into appliedFilters
  // so the loader re-runs against the backend.
  const handleFilter = () => {
    setAppliedFilters({ ...filters });
    setPage(1);
  };

  const fetchCustomers = () => loadCustomers();


  const toggleActive = async (userId, currentStatus) => {
    try {
      await fetch(`${API_URL}/api/admin/customers/${userId}/active`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !currentStatus })
      });
      fetchCustomers();
    } catch (error) {
      console.error('Error toggling customer status:', error);
    }
  };

  const performDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    setDeleteError('');
    try {
      const params = new URLSearchParams({ mode: deleteMode });
      if (deletePin) params.set('pin', deletePin);
      if (deleteBlacklist) { params.set('blacklist', 'true'); params.set('reason', 'Deleted by admin — abuse'); }
      const qs = `?${params.toString()}`;
      const res = await fetch(`${API_URL}/api/admin/customers/${confirmDelete.id}${qs}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setDeleteError(body.detail || `Delete failed (HTTP ${res.status})`);
        return;
      }
      setConfirmDelete(null);
      setDeletePin('');
      fetchCustomers();
      fetchStats();
    } catch (e) {
      setDeleteError(e.message || 'Network error');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">
      <div className="text-lg text-slate-600">Loading customers...</div>
    </div>;
  }

  // Build a query string from the current filter set so the export
  // endpoint receives the SAME visible rows the operator sees on screen.
  const exportQuery = (() => {
    const params = new URLSearchParams();
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v !== '' && v != null && v !== false) params.set(k, String(v));
    });
    return params.toString();
  })();

  return (
    <div className="p-4 md:p-6 w-full lg:w-4/5 mx-auto">
      <PageHeader
        icon={<Users className="w-5 h-5" />}
        title="Customers"
        subtitle="All registered accounts · current + imported legacy"
        testid="admin-customers-header"
        actions={
          <ExportMenu
            url={`${API_URL}/api/admin/customers/export${exportQuery ? `?${exportQuery}` : ''}`}
            token={token}
            filename="customers"
            testid="customers-export"
            label="Export customers"
          />
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center">
              <Users className="w-6 h-6 text-cyan-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Total Customers</p>
              <p className="text-2xl font-bold text-slate-800">{stats.total_customers || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">New (7 days)</p>
              <p className="text-2xl font-bold text-slate-800">{stats.new_customers_week || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Total Balance</p>
              <p className="text-2xl font-bold text-slate-800">€{stats.total_balance?.toFixed(2) || '0.00'}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Reserved</p>
              <p className="text-2xl font-bold text-slate-800">€{stats.total_reserved?.toFixed(2) || '0.00'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters — wrapped in shared FilterPanel. Basic: name/surname/
          email/country/account-id. Advanced: account picker, group,
          is_company, is_active, date range. Existing apply-on-Enter +
          handleFilter/clear semantics preserved verbatim. */}
      {(() => {
        const FILTER_LABELS = {
          first_name: 'First name', surname: 'Surname', email: 'Email',
          account_id: 'Acct ID', user_id: 'Account', country: 'Country',
          customer_group_id: 'Group', is_company: 'Type', is_active: 'Status',
          date_start: 'From', date_end: 'To',
        };
        const dropdownLabel = (k, v) => {
          if (k === 'user_id') {
            const a = pickerOptions.accounts.find((x) => x.user_id === v);
            return a ? (a.label || a.email || v) : v;
          }
          if (k === 'customer_group_id') {
            const g = pickerOptions.groups.find((x) => x.id === v);
            return g ? g.name : v;
          }
          if (k === 'is_company') return v === 'true' ? 'Company' : 'Individual';
          if (k === 'is_active')  return v === 'true' ? 'Active'  : 'Disabled';
          return String(v);
        };
        const activeChips = Object.entries(filters)
          .filter(([, v]) => v !== '' && v != null)
          .map(([k, v]) => ({
            key: k, label: FILTER_LABELS[k] || k, value: dropdownLabel(k, v),
            onRemove: () => {
              const next = { ...filters, [k]: '' };
              setFilters(next);
              // For dropdown-controlled filters, the effect at line 105 auto-
              // syncs appliedFilters. For text filters, we also push them
              // through immediately so the chip × works for typed fields.
              setAppliedFilters((prev) => ({ ...prev, [k]: '' }));
              setPage(1);
            },
          }));
        const advancedKeys = ['user_id', 'customer_group_id', 'is_company', 'is_active', 'date_start', 'date_end'];
        const advancedActiveCount = advancedKeys.filter((k) => filters[k]).length;
        const clearAll = () => {
          const cleared = { first_name: '', surname: '', email: '', account_id: '', user_id: '', country: '', is_company: '', is_active: '', customer_group_id: '', date_start: '', date_end: '' };
          setFilters(cleared); setAppliedFilters(cleared); setPage(1);
        };
        return (
          <div onKeyDown={(e) => { if (e.key === 'Enter') handleFilter(); }}>
          <FilterPanel
            open={showFilters}
            onToggle={() => setShowFilters((s) => !s)}
            activeChips={activeChips}
            onClearAll={clearAll}
            onApply={handleFilter}
            advancedOpen={advancedOpen}
            onToggleAdvanced={() => setAdvancedOpen((s) => !s)}
            advancedActiveCount={advancedActiveCount}
            testid="customers-filters"
            advancedChildren={
              <>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Account</label>
                  <select value={filters.user_id} onChange={(e) => setFilters({ ...filters, user_id: e.target.value })} data-testid="customers-filter-account" title="Jump to a specific customer account" className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white">
                    <option value="">Choose account</option>
                    {pickerOptions.accounts.map((a) => (<option key={a.user_id} value={a.user_id}>{a.label}{a.email ? ` · ${a.email}` : ''}</option>))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Group</label>
                  <select value={filters.customer_group_id} onChange={(e) => setFilters({ ...filters, customer_group_id: e.target.value })} data-testid="customers-filter-group" className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white">
                    <option value="">All groups</option>
                    {pickerOptions.groups.map((g) => (<option key={g.id} value={g.id}>{g.name}</option>))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Type</label>
                  <select value={filters.is_company} onChange={(e) => setFilters({ ...filters, is_company: e.target.value })} data-testid="customers-filter-is-company" className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white">
                    <option value="">Any type</option>
                    <option value="true">Company</option>
                    <option value="false">Individual</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Status</label>
                  <select value={filters.is_active} onChange={(e) => setFilters({ ...filters, is_active: e.target.value })} data-testid="customers-filter-is-active" className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white">
                    <option value="">Any status</option>
                    <option value="true">Active</option>
                    <option value="false">Disabled</option>
                  </select>
                </div>
                {/* Reserved-state filter (Phase H45) — quickly surface
                    customers whose `users.reserved` is negative (bug
                    fingerprint) or positive (has open reservations,
                    normal but worth spot-checking). */}
                <div>
                  <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Reserved</label>
                  <select
                    value={filters.reserved_state}
                    onChange={(e) => setFilters({ ...filters, reserved_state: e.target.value })}
                    data-testid="customers-filter-reserved"
                    title="Filter by users.reserved value"
                    className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white"
                  >
                    <option value="">Any</option>
                    <option value="positive">Has reserve (&gt;0)</option>
                    <option value="negative">Negative (bug!)</option>
                    <option value="nonzero">Any ≠ 0</option>
                  </select>
                </div>
                <DateRangeFilter
                  from={filters.date_start}
                  to={filters.date_end}
                  onChange={({ from, to }) => setFilters({ ...filters, date_start: from, date_end: to })}
                  label="Registered"
                  testidPrefix="customers-filter-date"
                />
              </>
            }
          >
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">First name</label>
              <input type="text" placeholder="First name ⏎" value={filters.first_name} onChange={(e) => setFilters({ ...filters, first_name: e.target.value })} data-testid="customers-filter-first-name" className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Surname</label>
              <input type="text" placeholder="Surname ⏎" value={filters.surname} onChange={(e) => setFilters({ ...filters, surname: e.target.value })} data-testid="customers-filter-surname" className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Email</label>
              <input type="text" placeholder="Email ⏎" value={filters.email} onChange={(e) => setFilters({ ...filters, email: e.target.value })} data-testid="customers-filter-email" className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Country</label>
              <select value={filters.country} onChange={(e) => setFilters({ ...filters, country: e.target.value })} data-testid="customers-filter-country" className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white">
                <option value="">All countries</option>
                {pickerOptions.countries.map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Acct ID</label>
              <input type="number" placeholder="Acct ID ⏎" value={filters.account_id} onChange={(e) => setFilters({ ...filters, account_id: e.target.value })} data-testid="customers-filter-account-id" className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white" />
            </div>
          </FilterPanel>
          </div>
        );
      })()}

      {/* Table — desktop only */}
      <div className="hidden md:block bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto max-h-[calc(100vh-340px)]">
          <table className="ar-zebra w-full">
            <thead className="sticky top-0 z-10 shadow-[0_2px_0_0_rgba(15,23,42,0.06)]">
              <tr className="bg-slate-200 text-slate-700 text-xs font-bold uppercase">
                <SortableHeader label="Account ID" field="account_id" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label="Legacy ID" field="legacy_id" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <th className="px-2.5 py-3 text-left whitespace-nowrap">Rank</th>
                <SortableHeader label="Role" field="role" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label="First Name" field="first_name" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label="Surname" field="surname" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label="Country" field="country" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label="Email" field="email" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label="Reg Date" field="reg_date" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label="Balance €" field="balance" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} align="right" className="hover:bg-slate-300/50" />
                <SortableHeader label="Recharged €" field="recharged_amount" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} align="right" className="hover:bg-slate-300/50" />
                <th className="px-2.5 py-3 text-right whitespace-nowrap" title="Cached live EVC credit balance. Click the row's refresh icon to re-poll EVC.">EVC bal.</th>
                <SortableHeader label="Company" field="is_company" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} align="center" className="hover:bg-slate-300/50" />
                <th className="px-2.5 py-3 text-center whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((customer, index) => (
                <tr key={customer.id} className={`border-b border-slate-100 hover:bg-cyan-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                  <td className="px-2.5 py-3 text-sm font-mono text-cyan-700 font-semibold whitespace-nowrap" data-testid={`customer-account-id-${customer.id}`}>
                    {customer.account_id || '—'}
                  </td>
                  <td className="px-2.5 py-3 text-sm font-mono whitespace-nowrap" data-testid={`customer-legacy-id-${customer.id}`}>
                    {customer.legacy_id ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-violet-100 text-violet-700 text-[11px] font-bold"
                            title={`Legacy PHP admin id ${customer.legacy_id}`}>
                        {customer.legacy_id}
                      </span>
                    ) : (
                      <span className="text-slate-300 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-2.5 py-3 text-sm whitespace-nowrap" data-testid={`customer-rank-${customer.id}`}>
                    {rankMap[customer.id] ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg font-bold font-mono text-xs bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 border border-amber-200"
                            title={`Score €${Number(rankMap[customer.id].score).toLocaleString(undefined, {maximumFractionDigits: 0})} / out of ${totalRanked.toLocaleString()}`}>
                        #{rankMap[customer.id].rank}
                      </span>
                    ) : (
                      <span className="text-slate-300 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-2.5 py-3 whitespace-nowrap">
                    <span className={`px-3 py-1 text-xs font-bold rounded-full ${
                      customer.role === 'user' ? 'bg-green-100 text-green-700'
                      : customer.role === 'admin' ? 'bg-amber-100 text-amber-700'
                      : customer.role === 'access_denied' ? 'bg-red-100 text-red-700'
                      : 'bg-slate-100 text-slate-600'
                    }`}>
                      {customer.role === 'access_denied' ? 'denied' : customer.role}
                    </span>
                    {customer.legacy_id && (
                      <span
                        className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-violet-100 text-violet-700"
                        title={`Legacy imported · legacy_id ${customer.legacy_id}`}
                        data-testid={`customer-legacy-badge-${customer.id}`}
                      >
                        legacy
                      </span>
                    )}
                    <CustomerBadges
                      isCompany={customer.is_company}
                      companyName={customer.company_name}
                      registrationDate={customer.registration_date}
                      newDays={newCustomerDays}
                      className="ml-1"
                      testid={`customer-badges-${customer.id}`}
                    />
                  </td>
                  <td className="px-2.5 py-3 text-sm text-slate-800 max-w-[140px] truncate" title={customer.first_name}>{customer.first_name}</td>
                  <td className="px-2.5 py-3 text-sm text-slate-800 max-w-[140px] truncate" title={customer.surname}>{customer.surname}</td>
                  <td className="px-2.5 py-3 text-sm text-slate-600 max-w-[120px] truncate" title={customer.country || ''}>{customer.country || '-'}</td>
                  <td className="px-2.5 py-3 text-sm text-slate-600 max-w-[220px] truncate" title={customer.email}>{customer.email}</td>
                  <td className="px-2.5 py-3 text-sm text-slate-600 whitespace-nowrap">
                    {new Date(customer.registration_date).toLocaleDateString()}
                  </td>
                  <td className="px-2.5 py-3 text-sm text-right font-bold text-slate-800 whitespace-nowrap tabular-nums">{customer.balance.toFixed(2)}</td>
                  <td className="px-2.5 py-3 text-sm text-right text-slate-600 whitespace-nowrap tabular-nums">{customer.recharged_amount.toFixed(2)}</td>
                  <td
                    className="px-2.5 py-3 text-sm text-right whitespace-nowrap tabular-nums"
                    data-testid={`customer-evc-balance-${customer.id}`}
                    title={
                      customer.evc_customer_id
                        ? (customer.evc_balance_synced_at
                            ? `EVC #${customer.evc_customer_id} · synced ${new Date(customer.evc_balance_synced_at).toLocaleString()}`
                            : `EVC #${customer.evc_customer_id} · never synced — open profile and click Sync.`)
                        : 'No EVC ID linked'
                    }
                  >
                    {customer.evc_customer_id == null ? (
                      <span className="text-slate-300">—</span>
                    ) : customer.evc_balance == null ? (
                      <span className="text-amber-600" title="EVC ID linked but balance not synced yet">⟳</span>
                    ) : (
                      <span className={`font-semibold ${Number(customer.evc_balance) > 0 ? 'text-emerald-700' : 'text-slate-500'}`}>
                        {customer.evc_balance}
                      </span>
                    )}
                  </td>
                  <td className="px-2.5 py-3 text-center whitespace-nowrap">
                    <span className={`px-3 py-1 text-xs font-bold rounded-full ${
                      customer.is_company ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {customer.is_company ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-2.5 py-3 text-center whitespace-nowrap">
                    <div className="inline-flex flex-col xl:flex-row gap-1.5 items-stretch xl:items-center justify-center">
                      <button
                        onClick={() => setProfileCustomerId(customer.id)}
                        className="px-3 py-1 text-xs font-bold rounded-lg bg-cyan-100 text-cyan-700 hover:bg-cyan-200 transition-colors flex items-center gap-1 justify-center"
                        data-testid={`customer-profile-${customer.id}`}
                        title="View / edit profile"
                      >
                        <Eye className="w-3 h-3" /> Profile
                      </button>
                      <button
                        onClick={() => toggleActive(customer.id, customer.is_active)}
                        className={`px-3 py-1 text-xs font-bold rounded-lg transition-colors ${
                          customer.is_active
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                        data-testid={`customer-toggle-active-${customer.id}`}
                      >
                        {customer.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      {customer.role !== 'admin' && (
                        <button
                          onClick={() => { setConfirmDelete(customer); setDeleteError(''); }}
                          className="px-3 py-1 text-xs font-bold rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center gap-1 justify-center"
                          data-testid={`customer-delete-${customer.id}`}
                          title="Delete this account permanently"
                        >
                          <Trash2 className="w-3 h-3" /> Delete account
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {customers.length === 0 && (
          <div className="text-center py-12 text-slate-600">
            No customers found. Import data to get started.
          </div>
        )}
      </div>

      {/* Mobile card stack */}
      <div className="md:hidden space-y-3" data-testid="customers-mobile-cards">
        {customers.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500 text-sm">
            No customers found.
          </div>
        ) : customers.map((customer) => {
          const rank = rankMap[customer.id];
          return (
            <div
              key={customer.id}
              data-testid={`customer-card-${customer.id}`}
              className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
            >
              <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200 flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">
                    {customer.account_id ? `ID #${customer.account_id}` : 'Customer'}
                  </p>
                  <p className="font-bold text-slate-800 text-sm flex items-center gap-1 min-w-0">
                    <span className="truncate min-w-0">{[customer.first_name, customer.surname].filter(Boolean).join(' ') || customer.email}</span>
                    <CustomerBadges
                      isCompany={customer.is_company}
                      companyName={customer.company_name}
                      registrationDate={customer.registration_date}
                      newDays={newCustomerDays}
                      testid={`customer-card-badges-${customer.id}`}
                    />
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Balance</p>
                  <p className="text-base font-bold text-slate-800 font-mono">€{customer.balance.toFixed(2)}</p>
                </div>
              </div>
              <dl className="px-4 py-3 text-sm divide-y divide-slate-100">
                <div className="flex justify-between py-1.5 gap-3 items-center">
                  <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">Email</dt>
                  <dd className="text-slate-800 text-right break-all text-xs">{customer.email}</dd>
                </div>
                <div className="flex justify-between py-1.5 gap-3 items-center">
                  <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">Role</dt>
                  <dd>
                    <span className={`px-2.5 py-0.5 text-[10px] font-bold rounded-full ${
                      customer.role === 'user' ? 'bg-green-100 text-green-700'
                      : customer.role === 'admin' ? 'bg-amber-100 text-amber-700'
                      : customer.role === 'access_denied' ? 'bg-red-100 text-red-700'
                      : 'bg-slate-100 text-slate-600'
                    }`}>
                      {customer.role === 'access_denied' ? 'denied' : customer.role}
                    </span>
                  </dd>
                </div>
                {rank && (
                  <div className="flex justify-between py-1.5 gap-3 items-center">
                    <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">Rank</dt>
                    <dd>
                      <span className="px-2 py-0.5 rounded-lg font-bold font-mono text-[11px] bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 border border-amber-200">
                        #{rank.rank}
                      </span>
                    </dd>
                  </div>
                )}
                <div className="flex justify-between py-1.5 gap-3 items-center">
                  <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">Country</dt>
                  <dd className="text-slate-700 text-right">{customer.country || '—'}</dd>
                </div>
                <div className="flex justify-between py-1.5 gap-3 items-center">
                  <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">Reg.</dt>
                  <dd className="text-slate-700 text-right text-xs">{new Date(customer.registration_date).toLocaleDateString()}</dd>
                </div>
                <div className="flex justify-between py-1.5 gap-3 items-center">
                  <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">Recharged</dt>
                  <dd className="text-slate-700 font-mono">€{customer.recharged_amount.toFixed(2)}</dd>
                </div>
                <div className="flex justify-between py-1.5 gap-3 items-center">
                  <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">Company</dt>
                  <dd>
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${customer.is_company ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                      {customer.is_company ? 'Yes' : 'No'}
                    </span>
                  </dd>
                </div>
              </dl>
              <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setProfileCustomerId(customer.id)}
                  data-testid={`customer-profile-mob-${customer.id}`}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-cyan-50 text-cyan-700 hover:bg-cyan-100"
                >
                  <Eye className="w-3.5 h-3.5" /> Profile
                </button>
                <button
                  onClick={() => toggleActive(customer.id, customer.is_active)}
                  data-testid={`customer-toggle-active-mob-${customer.id}`}
                  className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold ${customer.is_active ? 'bg-red-50 text-red-700 hover:bg-red-100' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}
                >
                  {customer.is_active ? 'Deactivate' : 'Activate'}
                </button>
                {customer.role !== 'admin' && (
                  <button
                    onClick={() => { setConfirmDelete(customer); setDeleteError(''); }}
                    data-testid={`customer-delete-mob-${customer.id}`}
                    className="px-3 py-2 rounded-lg text-xs font-bold bg-red-600 text-white hover:bg-red-700"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalCount > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4 bg-white rounded-xl border border-slate-200 px-4 py-3 shadow-sm" data-testid="customers-pagination">
          <div className="text-sm text-slate-600">
            Showing <strong>{((page - 1) * PAGE_SIZE) + 1}</strong>–<strong>{Math.min(page * PAGE_SIZE, totalCount)}</strong> of <strong>{totalCount.toLocaleString()}</strong> customers
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              data-testid="customers-pagination-prev"
              className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <span className="text-sm text-slate-700 font-medium px-2">
              Page <strong>{page}</strong> / {Math.max(1, Math.ceil(totalCount / PAGE_SIZE))}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(Math.ceil(totalCount / PAGE_SIZE), p + 1))}
              disabled={page >= Math.ceil(totalCount / PAGE_SIZE) || loading}
              data-testid="customers-pagination-next"
              className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Customer profile drawer */}
      {profileCustomerId && (
        <CustomerProfileDrawer
          token={token}
          customerId={profileCustomerId}
          onClose={() => setProfileCustomerId(null)}
          onSaved={() => { fetchCustomers(); fetchStats(); }}
        />
      )}

      {/* Confirm-delete modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" data-testid="confirm-delete-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-5 py-4 text-white flex items-center gap-3">
              <AlertTriangle className="w-5 h-5" />
              <h3 className="text-base font-bold">Delete account permanently?</h3>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-slate-700">
                Are you sure you want to delete this account?
              </p>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
                <p><span className="font-semibold text-slate-700">Name:</span> {confirmDelete.first_name} {confirmDelete.surname}</p>
                <p><span className="font-semibold text-slate-700">Email:</span> {confirmDelete.email}</p>
                <p><span className="font-semibold text-slate-700">Country:</span> {confirmDelete.country || '—'}</p>
                <p><span className="font-semibold text-slate-700">Balance:</span> €{(confirmDelete.balance || 0).toFixed(2)}</p>
              </div>
              {/* What will be removed */}
              <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="delete-preview">
                <div className="px-3 py-1.5 bg-slate-100 text-[11px] font-bold uppercase tracking-wide text-slate-600">
                  What this account has in the system
                </div>
                {!deletePreview ? (
                  <p className="p-3 text-xs text-slate-500">Loading…</p>
                ) : (
                  <div className="p-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    {Object.entries(deletePreview.counts || {}).map(([k, v]) => (
                      <div key={k} className="flex justify-between">
                        <span className="text-slate-500 capitalize">{k.replace(/_/g, ' ')}</span>
                        <span className={`font-mono font-bold ${v ? 'text-slate-800' : 'text-slate-300'}`}>{v}</span>
                      </div>
                    ))}
                    <div className="flex justify-between col-span-2 border-t border-slate-100 pt-1 mt-1">
                      <span className="text-slate-500">Completed orders · lifetime spent</span>
                      <span className="font-mono font-bold text-slate-800">
                        {deletePreview.completed_orders} · €{(deletePreview.lifetime_spent || 0).toFixed(2)}
                      </span>
                    </div>
                    {deletePreview.already_blacklisted && (
                      <p className="col-span-2 text-[11px] text-red-700 font-semibold">This e-mail is already blacklisted.</p>
                    )}
                  </div>
                )}
              </div>

              {/* Delete mode */}
              <div className="space-y-1.5">
                <label className="flex items-start gap-2 text-xs cursor-pointer">
                  <input type="radio" checked={deleteMode === 'account'} onChange={() => setDeleteMode('account')} data-testid="delete-mode-account" className="mt-0.5" />
                  <span>
                    <b>Delete account only</b> — orders, transactions and invoices stay in the
                    database for accounting and keep showing in analytics.
                  </span>
                </label>
                <label className="flex items-start gap-2 text-xs cursor-pointer">
                  <input type="radio" checked={deleteMode === 'purge'} onChange={() => setDeleteMode('purge')} data-testid="delete-mode-purge" className="mt-0.5" />
                  <span>
                    <b className="text-red-700">Full purge</b> — cascade-delete orders, transactions,
                    tickets, purchases, login history and e-mail log. Use for fake / abusive accounts.
                  </span>
                </label>
              </div>

              <label className="flex items-start gap-2 text-xs cursor-pointer bg-red-50 border border-red-200 rounded-lg p-2.5">
                <input type="checkbox" checked={deleteBlacklist} onChange={(e) => setDeleteBlacklist(e.target.checked)} data-testid="delete-blacklist" className="mt-0.5" />
                <span>
                  <b>Blacklist this e-mail</b> — {confirmDelete.email} will no longer be able to
                  register or log in. Manage the list via <b>Blocked e-mails</b>.
                </span>
              </label>

              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                ⚠️ This action cannot be undone.
              </p>
              {pinRequired && (
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-1">
                    PIN required
                  </label>
                  <input
                    type="password"
                    inputMode="numeric"
                    autoComplete="off"
                    value={deletePin}
                    onChange={(e) => setDeletePin(e.target.value.replace(/[^0-9]/g, ''))}
                    placeholder="Enter PIN"
                    data-testid="delete-pin-input"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Configure or change this PIN in <strong>Settings → PIN codes</strong>.
                  </p>
                </div>
              )}
              {deleteError && (
                <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5" data-testid="delete-error">
                  {deleteError}
                </p>
              )}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => { setConfirmDelete(null); setDeletePin(''); }}
                  disabled={deleting}
                  data-testid="confirm-delete-cancel"
                  className="flex-1 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={performDelete}
                  disabled={deleting}
                  data-testid="confirm-delete-yes"
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  {deleteMode === 'purge' ? 'Yes, purge everything' : 'Yes, delete account'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
